import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { api } from "../lib/api";
import { Seo } from "../components/Seo";
import { toast } from "sonner";
import { Check, Loader2, ShoppingBag } from "lucide-react";

const SIZES = ["S", "M", "L", "XL", "XXL"];
const aud = (c) => `$${(c / 100).toFixed(0)}`;

function ProductCard({ product }) {
  const [size, setSize] = useState("L");
  const [buying, setBuying] = useState(false);
  const sku = product.kind === "apparel" ? `${product.base}-${size}` : product.base;

  const buy = async () => {
    setBuying(true);
    try {
      const res = await api.post("/merch/checkout", {
        items: [{ sku, quantity: 1 }],
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Something jammed. Try again.");
      setBuying(false);
    }
  };

  return (
    <div className="group border border-hearth-line bg-hearth-surface hover:border-hearth-ochre transition-colors" data-testid={`merch-card-${product.base.toLowerCase()}`}>
      <div className="overflow-hidden">
        <img src={product.img} alt={product.name} loading="lazy"
          className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-700" />
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <h3 className="font-display text-2xl text-hearth-ink">{product.name}</h3>
          <p className="font-display text-2xl text-hearth-ochre">{aud(product.price_cents)}</p>
        </div>
        <p className="text-hearth-muted text-sm mt-2 leading-relaxed">{product.blurb}</p>
        {product.kind === "apparel" && (
          <div className="flex gap-2 mt-5" data-testid={`merch-sizes-${product.base.toLowerCase()}`}>
            {SIZES.map((s) => (
              <button key={s} type="button" data-testid={`merch-size-${product.base.toLowerCase()}-${s.toLowerCase()}`} onClick={() => setSize(s)}
                className={`w-9 h-9 text-xs font-semibold border transition-colors ${size === s ? "border-hearth-ember bg-hearth-ember text-hearth-ink" : "border-hearth-line text-hearth-muted hover:border-hearth-ochre"}`}>
                {s}
              </button>
            ))}
          </div>
        )}
        <button onClick={buy} disabled={buying} data-testid={`merch-buy-${product.base.toLowerCase()}`}
          className="mt-6 w-full inline-flex items-center justify-center gap-3 bg-hearth-ember hover:bg-hearth-ochre hover:text-hearth-bg disabled:opacity-30 text-hearth-ink text-xs font-semibold uppercase tracking-widest py-4 transition-colors">
          {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
          Buy {product.kind === "apparel" ? `— ${size}` : ""}
        </button>
      </div>
    </div>
  );
}

export function Merch() {
  const [catalog, setCatalog] = useState([]);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get("/merch").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const products = useMemo(() => {
    const seen = new Map();
    for (const row of catalog) if (!seen.has(row.base)) seen.set(row.base, row);
    return [...seen.values()];
  }, [catalog]);

  return (
    <div className="bg-hearth-bg min-h-screen" data-testid="merch-page">
      <Seo
        title="The Merch | No BS Counselling"
        description="Tees, caps, hoodies, pins, totes, stickers and stubby holders — every piece carries the hat-and-halo mark."
        path="/merch"
      />
      <Nav />
      <main className="pt-40 pb-24 max-w-7xl mx-auto px-6">
        <div className="flex flex-col items-center gap-6 text-center">
          <img src="/images/logo-mark.png" alt="The hat and halo mark" className="w-40 h-auto" data-testid="merch-logo" />
          <p className="eyebrow">The Merch</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-hearth-ink leading-tight max-w-3xl">
            Wear the hat. Carry the code.
          </h1>
          <p className="text-hearth-muted max-w-xl leading-relaxed">
            Every piece carries the mark — the hat, the quiet halo. Made properly, printed on demand,
            shipped anywhere in Australia. Merch is its own thing here — nothing to do with sessions,
            no strings, no upsell at the door.
          </p>
        </div>
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="merch-grid">
          {products.map((p) => <ProductCard key={p.base} product={p} />)}
        </div>
        <p className="mt-16 text-hearth-muted text-xs leading-relaxed max-w-xl">
          Printed and shipped on demand — allow a couple of weeks. Sizes run relaxed; if you're between
          sizes, go down. Every order funds the firewood, not a yacht.
        </p>
      </main>
      <Footer config={config} />
    </div>
  );
}

export function MerchSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!sessionId) { setChecking(false); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/merch/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setOrder(res.data.order);
          setChecking(false);
          return;
        }
      } catch { /* keep polling */ }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setChecking(false);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="bg-hearth-bg min-h-screen" data-testid="merch-success-page">
      <Nav />
      <main className="pt-40 pb-24 max-w-2xl mx-auto px-6">
        {checking && (
          <p className="text-hearth-muted flex items-center gap-3 font-essay italic text-xl" data-testid="merch-checking">
            <Loader2 className="w-5 h-5 animate-spin" /> Confirming with the bank…
          </p>
        )}
        {!checking && order && (
          <div data-testid="merch-confirmed">
            <Check className="w-10 h-10 text-hearth-ochre mb-6" strokeWidth={1.5} />
            <h1 className="font-display font-bold text-4xl text-hearth-ink">Yours. It's being made.</h1>
            <p className="mt-6 font-essay text-xl text-hearth-ink/90 leading-loose">
              Printed on demand and shipped to the address you gave. Give it a couple of weeks — good things and all that.
            </p>
            <div className="mt-8 border border-hearth-line bg-hearth-surface p-8 space-y-3" data-testid="merch-order-summary">
              {order.items.map((i) => (
                <div key={i.sku} className="flex justify-between text-sm">
                  <span className="text-hearth-ink">{i.quantity} × {i.name}{i.size ? ` (${i.size})` : ""}</span>
                  <span className="text-hearth-ochre">{aud(i.unit_price_cents * i.quantity)}</span>
                </div>
              ))}
              <div className="border-t border-hearth-line pt-3 flex justify-between font-semibold">
                <span className="text-hearth-ink">Total</span>
                <span className="text-hearth-ochre">{aud(order.total_cents)}</span>
              </div>
            </div>
          </div>
        )}
        {!checking && !order && (
          <div data-testid="merch-pending-note">
            <h1 className="font-display font-bold text-4xl text-hearth-ink">One moment.</h1>
            <p className="mt-6 font-essay text-xl text-hearth-ink/90">Payment's taking the long way round. If money left your card, the order is safe — Matt can see it on his side.</p>
          </div>
        )}
        <Link to="/merch" data-testid="merch-back-link" className="mt-10 inline-block border border-hearth-line hover:border-hearth-ochre text-hearth-muted hover:text-hearth-ink text-xs font-semibold uppercase tracking-widest px-8 py-4 transition-colors">
          Back to the Merch
        </Link>
      </main>
      <Footer />
    </div>
  );
}
